'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FileWithPreview } from '@/types';
import { createFilePreview, isValidFileSize, isValidFileType, revokeFilePreviews } from '@/lib/utils';
import { Button } from './Button';
import { X, Upload, File as FileIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FileUploadProps {
  onFilesChange: (files: FileWithPreview[]) => void;
  files: FileWithPreview[];
  maxFiles?: number;
  maxSizeMB?: number;
  allowedTypes?: string[];
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFilesChange,
  files,
  maxFiles = 2,
  maxSizeMB = 10,
  allowedTypes = ['application/pdf'],
}) => {
  const { t } = useTranslation();
  const [isClient, setIsClient] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The unmount cleanup below must see the list as it is at unmount, not as it
  // was at mount, so it reads the files through a ref that every render updates.
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  });

  useEffect(() => {
    setIsClient(true);
    return () => {
      // Cleanup previews on unmount
      revokeFilePreviews(filesRef.current);
    };
  }, []);

  const processFiles = useCallback(
    (fileList: FileList) => {
      if (files.length >= maxFiles) {
        alert(t('pageContent.home.uploadSection.form.fileUpload.errors.maxFiles', { maxFiles }));
        return;
      }

      setIsProcessing(true);
      const newFiles: FileWithPreview[] = [];

      try {
        for (const file of Array.from(fileList)) {
          if (files.length + newFiles.length >= maxFiles) break;

          if (!isValidFileType(file, allowedTypes)) {
            alert(t('pageContent.home.uploadSection.form.fileUpload.errors.invalidType', { types: allowedTypes.join(', ') }));
            continue;
          }

          if (!isValidFileSize(file, maxSizeMB)) {
            alert(t('pageContent.home.uploadSection.form.fileUpload.errors.maxSize', { maxSize: maxSizeMB }));
            continue;
          }

          newFiles.push(createFilePreview(file));
        }

        if (newFiles.length > 0) {
          onFilesChange([...files, ...newFiles]);
        }
      } catch (error) {
        alert(t('pageContent.home.uploadSection.form.fileUpload.errors.processing'));
      } finally {
        setIsProcessing(false);
      }
    },
    [files, maxFiles, maxSizeMB, allowedTypes, onFilesChange, t]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processFiles(e.dataTransfer.files);
      }
    },
    [processFiles]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(e.target.files);
      }
    },
    [processFiles]
  );

  const handleButtonClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const removeFile = useCallback(
    (index: number) => {
      const newFiles = [...files];
      revokeFilePreviews(newFiles.splice(index, 1));
      onFilesChange(newFiles);
    },
    [files, onFilesChange]
  );

  if (!isClient) {
    return (
      <div className="animate-pulse">
        <div className="h-40 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div
        className={`file-upload-area ${isDragging ? 'dragging' : ''} ${isProcessing ? 'opacity-50' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          accept={allowedTypes.join(',')}
          className="hidden"
          multiple={maxFiles > 1}
          disabled={isProcessing}
          aria-label="Select PDF files to upload"
        />
        <div className="flex flex-col items-center justify-center gap-2">
          <Upload className="w-12 h-12 text-gray-400" />
          <h3 className="text-lg font-medium">
            {isProcessing ? t('pageContent.home.uploadSection.form.fileUpload.processing') : t('pageContent.home.uploadSection.form.fileUpload.dragDrop')}
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            {t('pageContent.home.uploadSection.form.fileUpload.browse', { maxFiles, maxSize: maxSizeMB })}
          </p>
          <Button
            type="button"
            onClick={handleButtonClick}
            variant="outline"
            disabled={isProcessing}
          >
            {t('pageContent.home.uploadSection.form.fileUpload.selectFiles')}
          </Button>
        </div>
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={file.name}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <FileIcon className="w-5 h-5 text-primary-500" />
                <span className="text-sm text-gray-700">{file.name}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeFile(index)}
                className="text-gray-500 hover:text-red-500"
                aria-label={t('pageContent.home.uploadSection.form.fileUpload.removeFile')}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { FileUpload };